import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';
import ResourceCardList from './ResourceCardList';
import useIsPhone from '../utils/useIsPhone';

const AMBER     = '#F5A623';
const FONT_BODY = "'DM Sans', sans-serif";

const toggleBtnStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px',
  padding: '10px 14px', cursor: 'pointer', fontFamily: FONT_BODY, fontSize: '0.82rem', fontWeight: 700,
  color: AMBER, textAlign: 'left',
};
// Phones (<= 640px): 44px toggles and 14px text; desktop unchanged.
const phoneToggle = { ...toggleBtnStyle, minHeight: '44px', fontSize: '0.875rem' };

// Lightweight "Schools that teach this / Unions that represent this" cross-links for a
// trade_career_data trade -- reuses ResourceCardList for identical card rendering to the Trade
// Hub, but stays inline inside Career Pay Guide's detail view rather than opening the full
// overlay (no licensing content here).
//
// `categories` is trade_career_data.resource_directory_categories, read straight off the row
// rather than re-derived here. It replaces a hardcoded trade -> category map that had gone
// stale: resource_directory's category vocabulary was renamed under it ("Aviation" ->
// "Aviation Mechanic", "Maintenance" -> "Facilities Maintenance", "CNC"/"Machining" ->
// "CNC & Machining"), so 7 mapped trades silently resolved to zero rows while another 10 were
// missing from the map entirely -- 17 of 37 trades showed no section at all.
//
// A trade with no mapping is a real, expected state, not an error: 5 trades have no Illinois
// program in the directory yet. Those get an explicit "no programs listed" line instead of the
// section vanishing, so the absence reads as verified rather than broken. Still renders nothing
// while loading, to avoid flashing an empty state that immediately fills in.
export default function TradeCrossLinks({ trade, categories }) {
  const isPhone = useIsPhone();
  const { t } = useTranslation();
  const mapped = Array.isArray(categories) ? categories.filter(Boolean) : [];
  const mappedKey = mapped.join('|');
  const [schools, setSchools] = useState([]);
  const [unions, setUnions] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [schoolsOpen, setSchoolsOpen] = useState(false);
  const [unionsOpen, setUnionsOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSchoolsOpen(false);
    setUnionsOpen(false);
    if (!mappedKey) { setSchools([]); setUnions([]); setLoaded(true); return; }
    setLoaded(false);
    (async () => {
      // One query for both types -- category = ANY(mapped) AND is_active AND type IN
      // ('school','union') -- then split client-side, so a multi-category trade like Heavy
      // Equipment Operator ("Equipment" + "Operating Engineers") pulls from every mapped
      // category in a single round trip.
      const { data } = await supabase
        .from('resource_directory')
        .select('*')
        .in('category', mappedKey.split('|'))
        .in('type', ['school', 'union'])
        .eq('is_active', true)
        .order('name', { ascending: true });
      if (cancelled) return;
      const rows = data || [];
      setSchools(rows.filter(r => r.type === 'school'));
      setUnions(rows.filter(r => r.type === 'union'));
      setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [trade, mappedKey]);

  if (!loaded) return null;

  if (schools.length === 0 && unions.length === 0) {
    return (
      <p style={{
        fontSize: isPhone ? '0.875rem' : '0.78rem', color: isPhone ? 'rgba(255,255,255,0.68)' : 'rgba(255,255,255,0.38)', lineHeight: 1.4,
        marginBottom: '20px', fontFamily: FONT_BODY,
      }}>
        {t('resources.crossLinks.noPrograms')}
      </p>
    );
  }

  // schools.length counts resource_directory rows, i.e. individual programs -- a college with
  // 3 automotive programs contributes 3 rows. Showing that alone as "Schools" undercounts how
  // few distinct institutions there really are, so the label needs both numbers, not just one.
  // Unions never have this issue (verified: no union name has more than one active row today),
  // so its count stays a plain row count.
  const collegeCount = new Set(schools.map(r => r.name)).size;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
      {schools.length > 0 && (
        <div>
          <button onClick={() => setSchoolsOpen(o => !o)} style={isPhone ? phoneToggle : toggleBtnStyle}>
            <span>
              {t('resources.crossLinks.schoolsThatTeach')} ({t('resources.collegeCount', { count: collegeCount })}, {t('resources.programCount', { count: schools.length })})
            </span>
            <span style={{ flexShrink: 0, marginLeft: '10px' }}>{schoolsOpen ? '▲' : '▼'}</span>
          </button>
          {schoolsOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '10px' }}>
              <ResourceCardList resources={schools} type="school" />
            </div>
          )}
        </div>
      )}
      {unions.length > 0 && (
        <div>
          <button onClick={() => setUnionsOpen(o => !o)} style={isPhone ? phoneToggle : toggleBtnStyle}>
            <span>{t('resources.crossLinks.unionsThatRepresent', { count: unions.length })}</span>
            <span style={{ flexShrink: 0, marginLeft: '10px' }}>{unionsOpen ? '▲' : '▼'}</span>
          </button>
          {unionsOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '10px' }}>
              <ResourceCardList resources={unions} type="union" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
